/**
 * Redirecciones 301 de URLs del portal con ubicaciones que ya no existen.
 *
 * Tras fusionar ubicaciones duplicadas (fase 2), los slugs antiguos quedan
 * como alias: /portal/traspaso/todas/zona-sur-madrid → …/madrid?comarcas=zona-sur,
 * …/alicante/elche → …/alicante/elx. Aquí solo se construye la URL nueva; la
 * consulta de alias se inyecta (`geo_slug_principal` en el servidor).
 */

/** Segmento comodín del portal (igual que `WILDCARD` en portal-url.ts). */
export const WILDCARD = "todas";

export type AliasNivel = "provincia" | "ciudad" | "barrio";
export type AliasDestino = { slug: string; comarca?: string };
export type AliasResolver = (
  nivel: AliasNivel,
  slug: string,
  padreSlug: string | null,
) => Promise<AliasDestino | null>;

/** Parámetros de búsqueda con ubicaciones por slug. */
const QUERY_NIVEL: Record<string, AliasNivel> = {
  provs: "provincia",
  ciudades: "ciudad",
  barrios: "barrio",
};
/** Posición en el path: /portal/:op/:uso/:provincia/:ciudad/:barrio */
const PATH_NIVEL: Array<[number, AliasNivel]> = [
  [2, "provincia"],
  [3, "ciudad"],
  [4, "barrio"],
];

/** Parámetros de búsqueda como texto (el router convierte "100" en número). */
export function searchStrings(search: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries((search ?? {}) as Record<string, unknown>)) {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
      out[k] = String(v);
    }
  }
  return out;
}

/** ¿Hay algo en la URL que pueda ser un slug antiguo? */
export function puedeTenerAlias(segments: string[], search: Record<string, unknown>): boolean {
  return (
    PATH_NIVEL.some(([i]) => !!segments[i] && segments[i] !== WILDCARD) ||
    Object.keys(QUERY_NIVEL).some((k) => typeof search[k] === "string" && search[k] !== "")
  );
}

/**
 * Devuelve el path y la búsqueda con los slugs principales, o `null` si no
 * hay nada que cambiar. Conserva el resto de parámetros.
 */
export async function urlPorAlias(
  segments: string[],
  search: Record<string, unknown>,
  resolve: AliasResolver,
): Promise<{ segments: string[]; search: Record<string, string> } | null> {
  const segs = segments.map((s) => s.toLowerCase());
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(search)) {
    if (v == null || v === "") continue;
    out[k] = typeof v === "string" ? v : String(v);
  }
  let cambiado = false;
  const comarcas = new Set(
    (out.comarcas ?? "").split(",").filter(Boolean),
  );

  const traducir = async (nivel: AliasNivel, slug: string, padre: string | null) => {
    const d = await resolve(nivel, slug, padre);
    if (!d || d.slug === slug) return slug;
    cambiado = true;
    if (d.comarca) comarcas.add(d.comarca);
    return d.slug;
  };

  let padre: string | null = null;
  for (const [i, nivel] of PATH_NIVEL) {
    const s = segs[i];
    if (!s || s === WILDCARD) {
      padre = null;
      continue;
    }
    segs[i] = await traducir(nivel, s, nivel === "barrio" ? null : padre);
    padre = segs[i];
  }

  for (const [k, nivel] of Object.entries(QUERY_NIVEL)) {
    if (!out[k]) continue;
    const slugs: string[] = [];
    for (const s of out[k].split(",").filter(Boolean)) {
      slugs.push(await traducir(nivel, s.toLowerCase(), null));
    }
    out[k] = Array.from(new Set(slugs)).join(",");
  }

  if (!cambiado) return null;
  if (comarcas.size) out.comarcas = Array.from(comarcas).join(",");
  return { segments: segs, search: out };
}
